import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import * as styles from './range-calendar.module.scss'; // Make sure you create this CSS file
import { BasicButton } from '../../assets/common';
import { next_icon, prev_icon } from '../../assets/img';

const CURRENT_DATE = moment(new Date());
const FIRST_WEEK_INDEX = 1;
const MIN_DATE_PREV_MONTH = 23;
const MAX_DATE_NEXT_MONTH = 13;
const LAST_WEEK_INDEX = 5;

const RangeCalendar = ({ value, onChange, children }) => {
  const [currentMonth, setCurrentMonth] = useState(CURRENT_DATE);
  const [selectedDates, setSelectedDates] = useState([null, null]);

  useEffect(() => {
    setSelectedDates(value || [null, null]);
    if (value[0]) {
      setCurrentMonth(moment(value[0]));
    }
  }, [value]);

  const currentMonthDates = useMemo(() => {
    const current = generateCalendarChunk(currentMonth);
    const next = generateCalendarChunk(getNextMonth(currentMonth));
    return [current, next];
  }, [currentMonth]);

  const handlePrev = () => {
    const newCurrentMonth = getPrevMonth(currentMonth);
    setCurrentMonth(newCurrentMonth);
  };

  const handleNext = () => {
    const newCurrentMonth = getNextMonth(currentMonth);
    setCurrentMonth(newCurrentMonth);
  };

  const handleClickDateCell = (date, isCurrentMonth) => {
    const momentDate = isCurrentMonth ? moment(currentMonth).date(date) : moment(getNextMonth(currentMonth)).date(date);
    const newSelectedDates = [...selectedDates];
    if (!selectedDates[0]) {
      newSelectedDates[0] = momentDate;
    } else if (!selectedDates[1]) {
      if (momentDate.isAfter(selectedDates[0])) {
        newSelectedDates[1] = momentDate;
      } else {
        newSelectedDates[1] = selectedDates[0];
        newSelectedDates[0] = momentDate;
      }
    } else {
      newSelectedDates[0] = momentDate;
      newSelectedDates[1] = null;
    }
    setSelectedDates(newSelectedDates);
    onChange(newSelectedDates);
  };

  const isDisabled = (date, weekIndex) => {
    //disabled day not in month
    return (weekIndex <= FIRST_WEEK_INDEX && date >= MIN_DATE_PREV_MONTH) || (weekIndex >= LAST_WEEK_INDEX && date <= MAX_DATE_NEXT_MONTH);
  };

  const getClassDateSelected = (date, mIdx, wIdx) => {
    if (isDisabled(date, wIdx)) return '';
    const momentDate = mIdx === 0 ? moment(currentMonth).date(date) : moment(getNextMonth(currentMonth)).date(date);
    const isNotSelectFull = !selectedDates[0] || !selectedDates[1];
    const isStartDate = selectedDates[0] && momentDate.isSame(selectedDates[0], 'day');
    const isEndDate = selectedDates[1] && momentDate.isSame(selectedDates[1], 'day');
    if (isStartDate && isEndDate) return styles.circle;
    if (isStartDate) return `${styles.startDate} ${isNotSelectFull ? styles.circle : ''}`;
    if (isEndDate) return `${styles.endDate} ${isNotSelectFull ? styles.circle : ''}`;
    const isWithinRange = momentDate.isBetween(selectedDates[0], selectedDates[1], 'day', '[]');
    if (isWithinRange) return styles.selected;
    return '';
  };

  const isToday = (date, mIdx) => {
    const baseDate = mIdx === 0 ? currentMonth : getNextMonth(currentMonth);
    const dateMoment = moment(baseDate).date(date);
    return dateMoment.isSame(CURRENT_DATE, 'day');
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.months}>
        <div className={styles.centerLine} />
        {currentMonthDates.map((month, mIdx) => (
          <div key={mIdx} className={styles.month}>
            {!mIdx ? (
              <div className={styles.topBarLeft}>
                <BasicButton mode="icon" className={styles.prevNextBtn} onClick={handlePrev}>
                  <img src={prev_icon} alt="next" />
                </BasicButton>
                <div className={styles.monthYear}>{moment(currentMonth).format('MMMM YYYY')}</div>
              </div>
            ) : (
              <div className={styles.topBarRight}>
                <div className={styles.monthYear}>{moment(getNextMonth(currentMonth)).format('MMMM YYYY')}</div>
                <BasicButton mode="icon" className={styles.prevNextBtn} onClick={handleNext}>
                  <img src={next_icon} alt="next" />
                </BasicButton>
              </div>
            )}
            <div className={styles.calendar}>
              {month.map((week, wIdx) => (
                <React.Fragment key={wIdx}>
                  {wIdx === 0 ? (
                    <div className={styles.dayNames}>
                      {week.map((dateName, index) => (
                        <div key={index} className={styles.dayNameCell}>
                          {dateName}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`${styles.dates}`}>
                      {week.map((date, dIdx) => {
                        const selectedClass = getClassDateSelected(date, mIdx, wIdx);
                        const disabled = isDisabled(date, wIdx);
                        const isCurrentDay = isToday(date, mIdx);
                        return (
                          <div
                            key={dIdx}
                            className={`${styles.dayCell} ${selectedClass}`}
                            onClick={() => {
                              if (disabled) return;
                              handleClickDateCell(date, mIdx === 0);
                            }}
                          >
                            <span className={`${selectedClass} ${disabled ? styles.disabled : ''}`}>{date}</span>
                            {!disabled && isCurrentDay && <span className={styles.currentDay} />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
      <>{children}</>
    </div>
  );
};

function generateCalendarChunk(momentDate) {
  const date = momentDate.clone();
  const firstDayOfMonth = date.clone().startOf('month');
  const lastDayOfMonth = date.clone().endOf('month');
  const numDaysInMonth = lastDayOfMonth.date();

  let firstDayOfWeek = firstDayOfMonth.day();
  firstDayOfWeek = (firstDayOfWeek + 6) % 7;

  const lastDayOfPrevMonth = firstDayOfMonth.clone().subtract(1, 'month').endOf('month');
  const numDaysInPrevMonth = lastDayOfPrevMonth.date();

  const calendar = [];

  let dayCounter = numDaysInPrevMonth - firstDayOfWeek + 1;
  for (let i = 0; i < firstDayOfWeek; i++) {
    calendar.push(dayCounter);
    dayCounter++;
  }

  for (let i = 1; i <= numDaysInMonth; i++) {
    calendar.push(i);
  }

  const remainingDays = 6 * 7 - calendar.length;
  for (let i = 1; i <= remainingDays; i++) {
    calendar.push(i);
  }

  const chunkedCalendar = [['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']];
  for (let i = 0; i < calendar.length; i += 7) {
    chunkedCalendar.push(calendar.slice(i, i + 7));
  }

  return chunkedCalendar;
}

function getPrevMonth(momentDate) {
  return momentDate.clone().subtract(1, 'month');
}

function getNextMonth(momentDate) {
  return momentDate.clone().add(1, 'month');
}

export default RangeCalendar;
